import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { useOnboarding, OnboardingStep } from '../../contexts/OnboardingContext';
import SecurityIcon from '@mui/icons-material/Security';
import SettingsIcon from '@mui/icons-material/Settings';
import PeopleIcon from '@mui/icons-material/People';
import { useBusinessTheme } from '../../contexts/BusinessThemeContext';

const RolesGuide: React.FC = () => {
  const { isOnboarding, currentStep, isAdminUser } = useOnboarding();
  const { theme: businessTheme } = useBusinessTheme();

  // Only show for admin users in the Roles step
  if (!isOnboarding || 
      currentStep !== OnboardingStep.Roles || 
      !isAdminUser) {
    return null;
  }

  // Use business theme colors
  const primaryColor = businessTheme?.navigationColor || '#2196f3';
  const backgroundColor = businessTheme?.subtleAccent || '#f5f7fa';
  const textColor = businessTheme?.textColor || '#333333';

  return (
    <Box sx={{ position: 'relative', mb: 8 }}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: 3, 
          mt: 2, 
          borderLeft: `4px solid ${primaryColor}`, 
          bgcolor: backgroundColor,
          color: textColor
        }}
      >
        <Typography variant="h6" sx={{ mb: 1, fontWeight: "bold" }}>
          Set Up User Roles
        </Typography>
        <Typography variant="body1" paragraph>
          Create roles to define what different team members can do within your business:
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <SecurityIcon sx={{ mr: 1, color: primaryColor }} />
          <Typography variant="body1" fontWeight="medium">
            Access Control
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ mb: 3, pl: 4 }}>
          Define which features and data each role can access. For example, you might want some staff to only view appointments but not create or edit them.
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <SettingsIcon sx={{ mr: 1, color: primaryColor }} />
          <Typography variant="body1" fontWeight="medium">
            Administrative Privileges
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ mb: 3, pl: 4 }}>
          Determine which roles can manage business settings, billing, and other administrative functions.
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <PeopleIcon sx={{ mr: 1, color: primaryColor }} />
          <Typography variant="body1" fontWeight="medium">
            Common Roles
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ pl: 4 }}>
          Examples include Admin (full access), Manager (can manage appointments and staff), and Staff (limited to handling their own appointments).
        </Typography>
      </Paper>
    </Box>
  );
};

export default RolesGuide; 